import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApi } from '../../../../api/api'
import { ROUTES } from '../../../../router/routes'

import {
    ScoopDialog,
    ScoopButton,
    ScoopText,
    ScoopLinearProgress,
    ScoopCheckbox,
    ScoopSelector,
    useToast,
} from '../../../../components/ui'
import { EnvelopeOpenIcon } from '@phosphor-icons/react'

import EmailPreviewModal from './PreviewEmail'

import { replace } from 'lodash'

import datasourceDefs from '../../../../api/datasourceConfig'
import styles from './GMail.module.scss'

const bot = datasourceDefs.find((b) => b.name === 'Gmail')

// google api, add redirect to this in code based on the current url
const GMAIL_API_URL = bot.GMAIL_API_URL
const REDIRECT_URI = `${window.location.protocol}//${window.location.host}/gmail`

export const GMail = () => {
    const toast = useToast()
    const navigate = useNavigate()
    const { postData: postGmailAuth } = useApi(bot.API_URL)
    const { postData: postRobotActions } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/botactions'
    )
    const [isLoading, setIsLoading] = useState(false)
    const [oauthCode, setOauthCode] = useState('')
    const [userName, setUserName] = useState('') //this is the username returned by the first email scan call, since we don't know what gmail user the user selected
    const [inboxID, setInboxID] = useState('')
    const [fromSender, setFromSender] = useState('')
    const [emailSubject, setEmailSubject] = useState('')
    const [subjectOnly, setSubjecOnly] = useState(true)
    const [emailsList, setEmailsList] = useState([])

    const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false)
    const [rawEmail, setRawEmail] = useState('')
    // this flow means the user came in via the setup page and now has to authorize gmail
    const [isFinishSetupFlow, setIsFinishSetupFlow] = useState(false)
    const [isReadyToSave, setIsReadyToSave] = useState(false)

    const location = useLocation()
    const { inboxInfo } = location.state || {} // Ensure there's a fallback if state is undefined

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        const code = queryParams.get('code')

        if (code) {
            const decodedCode = decodeURIComponent(code)
            setOauthCode(decodedCode)
            setIsFinishSetupFlow(false)
            const stateObject = JSON.parse(localStorage.getItem('gmailIntegrationState'))
            setInboxID(stateObject.inboxID)
            setEmailSubject(stateObject.emailSubject)
            setFromSender(stateObject.emailFrom)
        } else if (queryParams.toString() !== '') {
            // console.log("Got back from Google with other parameters:", queryParams.toString());
            setIsFinishSetupFlow(false)
        }

        if (inboxInfo != {}) {
            setIsFinishSetupFlow(true)
        }
    }, []) // Empty dependency array ensures this runs once on mount

    // Note: this used to work but we took it out of the current flow
    const scanEmails = async () => {
        const action = {
            action: 'scanEmails',
            emailSubject: emailSubject,
            emailFrom: fromSender,
            max_days_ago: '',
            userName: userName,
            subjectOnly: subjectOnly,
            redirect_uri: REDIRECT_URI,
            oauthCode: oauthCode,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postGmailAuth(action)
        return result
    }

    const handleScanEmails = async () => {
        setIsLoading(true)
        try {
            const data = await scanEmails()
            if (data.status === 'success') {
                setEmailsList(data?.emails)
                setUserName(data?.userName)
                setIsReadyToSave(true)
            } else {
                console.log('error reading emails!')
            }
        } catch (error) {
            console.log(error)
        }
        setIsLoading(false)
    }

    const previewEmail = async (emailID) => {
        const action = {
            action: 'previewEmail',
            userName: userName,
            emailID: emailID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postGmailAuth(action)
        return result
    }

    const handleEmailClick = async (emailID) => {
        setIsLoading(true)
        try {
            const data = await previewEmail(emailID)
            setRawEmail(data?.email)
            setEmailPreviewModalOpen(true)
        } catch (error) {
            console.log(error)
        }
        setIsLoading(false)
    }

    const commenceGMailFlow = () => {
        // save the local state so we can come back to it after the oauth dance
        localStorage.setItem(
            'gmailIntegrationState',
            JSON.stringify({
                inboxID: inboxInfo.inboxID,
                emailFrom: inboxInfo.emailFrom,
                emailSubject: inboxInfo.emailSubject,
            })
        )

        window.location.href = replace(GMAIL_API_URL, 'REDIRECT_URI', REDIRECT_URI)
    }

    function handleClose(event) {
        setTimeout(() => navigate(ROUTES.SOURCES), 5000)
    }

    const createBot = async () => {
        const action = {
            action: 'flipOnSetupInbox',
            inboxID: inboxID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }

        const result = await postRobotActions(action)
        return result
    }

    const handleSave = () => {
        createBot().then((results) => {
            if (results?.status === 'success') {
                toast.success('Gmail connector created successfully')
                setTimeout(() => navigate(ROUTES.SOURCES), 5000)
            } else {
                toast.error(results?.message ?? 'Failed to create Gmail app connector')
                setTimeout(() => navigate(ROUTES.SOURCES), 5000)
            }
        })
    }
    // Convert frequency to options for ScoopSelector
    const frequencyOptions = [{ label: 'daily', value: 'daily' }]

    const actions = (
        <div className={styles.actionsContainer}>
            <ScoopButton variant="grey" onClick={handleClose}>
                Cancel
            </ScoopButton>
            {oauthCode && !isReadyToSave && (
                <ScoopButton onClick={handleScanEmails}>Verify</ScoopButton>
            )}
            {oauthCode && isReadyToSave && <ScoopButton onClick={handleSave}>Save</ScoopButton>}
        </div>
    )

    return (
        <>
            <EmailPreviewModal
                rawEmail={rawEmail}
                open={emailPreviewModalOpen}
                onClose={() => setEmailPreviewModalOpen(false)}
            />
            {isFinishSetupFlow && (
                <ScoopDialog
                    open={true}
                    title={'Gmail Integration'}
                    actions={actions}
                    icon={bot.icon}
                    maxWidth={'800px'}
                >
                    <div className={styles.dialogContainer}>
                        <div className={styles.dialogContent}>
                            {!oauthCode && (
                                <div>
                                    <div className={styles.descriptionText}>
                                        Since the user chose to integrate via gmail, tell them...
                                    </div>
                                    <button
                                        onClick={commenceGMailFlow}
                                        className={styles.copyButton}
                                    >
                                        <img
                                            src={bot.button}
                                            alt="Integrate GMail"
                                            className={styles.buttonIcon}
                                        />
                                    </button>
                                </div>
                            )}

                            {oauthCode && (
                                <>
                                    <div className={styles.infoBox}>
                                        <ScoopText>
                                            {' '}
                                            Subject: <b>{emailSubject}</b> From: <b>{fromSender}</b>
                                        </ScoopText>
                                    </div>
                                    <ScoopSelector
                                        value="daily"
                                        label="How often do you want to check this email?"
                                        options={frequencyOptions}
                                    />
                                    <div className={styles.checkboxGroup}>
                                        <ScoopCheckbox
                                            label="Scoop historical data from previous emails"
                                            defaultChecked
                                        />
                                    </div>
                                </>
                            )}

                            {emailsList && emailsList.length > 0 && (
                                <ul className={styles.emailList}>
                                    {emailsList.map((email) => (
                                        <li className={styles.emailListItem} key={email.emailID}>
                                            <button
                                                className={styles.emailListButton}
                                                onClick={() => handleEmailClick(email.emailID)}
                                            >
                                                <EnvelopeOpenIcon
                                                    size={20}
                                                    className={styles.emailIcon}
                                                />
                                                <span>{email.snippet}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {isLoading && <ScoopLinearProgress className={styles.progressBar} />}
                        </div>
                    </div>
                </ScoopDialog>
            )}
        </>
    )
}
